// Angular
import { Injectable } from '@angular/core';

/**
 * Current preference values needed to resolve a toggle
 */
export interface IWageringPreferenceModel {
  isClearWager: boolean;
  requireConfirmation: boolean;
  isExoticSelections: boolean;
  eventTypeFilter: string[];
}

/**
 * Field/value pair to upsert (with the recomputed filter for event types)
 */
export interface IWageringPreferenceUpdate {
  fieldName: string;
  value: string;
  eventTypeFilter?: string[];
}

/**
 * Pure business logic for the Wagering Preferences component.
 * Keeps the component thin — it only wires the facade + view state.
 */
@Injectable({ providedIn: 'root' })
export class WageringPreferencesService {
  // Selectable event types (the UI keys, lower-case)
  readonly eventTypes: string[] = [
    'thoroughbred',
    'harness',
    'greyhound',
    'international'
  ];

  /**
   * Resolve the upsert payload for a preference toggle, or null when blocked
   * @param {string} type UI key of the toggled preference
   * @param {IWageringPreferenceModel} model Current preference values
   * @returns {IWageringPreferenceUpdate | null} Field/value to save, or null
   */
  resolveUpdate(
    type: string,
    model: IWageringPreferenceModel
  ): IWageringPreferenceUpdate | null {
    if (this.eventTypes.includes(type)) {
      if (this.isEventTypeToggleBlocked(type, model.eventTypeFilter)) return null;

      const eventTypeFilter = this.toggleEventType(type, model.eventTypeFilter);
      return {
        fieldName: 'EventTypeFilter',
        value: JSON.stringify(eventTypeFilter),
        eventTypeFilter
      };
    }

    switch (type) {
      case 'name':
        return { fieldName: 'SortBy', value: 'Name' };
      case 'mtp':
        return { fieldName: 'SortBy', value: 'MTP' };
      case 'clean-on-wager':
        return { fieldName: 'IsClearWager', value: model.isClearWager ? 'false' : 'true' };
      case 'require-confirmation':
        return { fieldName: 'RequireConfirmation', value: model.requireConfirmation ? 'false' : 'true' };
      case 'keep-exotic-selections':
        return { fieldName: 'IsExoticSelections', value: model.isExoticSelections ? 'false' : 'true' };
      default:
        return null;
    }
  }

  /**
   * Block deselecting the last remaining event type (thoroughbred counts as two)
   * @param {string} type UI key of the toggled event type
   * @param {string[]} filter Current EventTypeFilter
   * @returns {boolean} True when the toggle must be ignored
   */
  isEventTypeToggleBlocked(type: string, filter: string[]): boolean {
    if (!this.eventTypes.includes(type)) return false;

    const capitalized = this.capitalize(type);

    if (filter.length === 1 && filter.includes(capitalized) && type !== 'thoroughbred')
      return true;

    if (filter.length === 2 && filter.includes(capitalized) && type === 'thoroughbred')
      return true;

    return false;
  }

  /**
   * Return a new EventTypeFilter with the given type toggled on/off
   * @param {string} type UI key of the toggled event type
   * @param {string[]} filter Current EventTypeFilter
   * @returns {string[]} Recomputed filter
   */
  toggleEventType(type: string, filter: string[]): string[] {
    const copy = [...(filter ?? [])];
    const eventType = this.capitalize(type);

    if (eventType === 'Thoroughbred')
      return this.toggleThoroughbredAndQuarterhorse(copy);

    const index = copy.indexOf(eventType);
    if (index > -1) copy.splice(index, 1);
    else copy.push(eventType);

    return copy;
  }

  /**
   * Toggle Thoroughbred + Quarterhorse together (they move as a pair)
   * @param {string[]} copy Working copy of the filter
   * @returns {string[]} Updated filter
   */
  private toggleThoroughbredAndQuarterhorse(copy: string[]): string[] {
    const hasThoroughbred = copy.includes('Thoroughbred');
    const hasQuarterhorse = copy.includes('Quarterhorse');

    if (hasThoroughbred && hasQuarterhorse)
      return copy.filter((x) => x !== 'Thoroughbred' && x !== 'Quarterhorse');

    if (!hasThoroughbred) copy.push('Thoroughbred');
    if (!hasQuarterhorse) copy.push('Quarterhorse');

    return copy;
  }

  /**
   * Capitalize the first letter, lower-case the rest
   * @param {string} value Raw value
   * @returns {string} Capitalized value
   */
  private capitalize(value: string): string {
    return value[0].toUpperCase() + value.slice(1).toLowerCase();
  }
}
